import { ArrowLeft, CircleAlert, UserRoundX } from "lucide-react";
import { createPortal } from "react-dom";

import Index from "@/pages/Index";
import DemoRoleDialog from "./DemoRoleDialog";
import { TokenLoginSpinner } from "./TokenLoginSpinner";

const MEDITONG_RETURN_URL =
  "https://www.meditong.com/renewal/index_new.asp";

export type TokenLoginStatus =
  | "pending"
  | "select"
  | "unauthorized"
  | "error";

type TokenLoginViewProps = {
  readonly status: TokenLoginStatus;
  readonly unauthorizedMessage: string;
  readonly demoRoles: ReadonlyArray<{
    readonly role: string;
    readonly label: string;
  }>;
  readonly switchingRole: string | null | undefined;
  readonly onSelectRole: (role?: string) => void;
};

export function TokenLoginView({
  status,
  unauthorizedMessage,
  demoRoles,
  switchingRole,
  onSelectRole,
}: TokenLoginViewProps) {
  const content = (() => {
    switch (status) {
      case "select":
        return (
          <DemoRoleDialog
            open
            roles={demoRoles}
            activeRole={undefined}
            switchingRole={switchingRole}
            onSelect={(role) => onSelectRole(role ?? undefined)}
          />
        );
      case "unauthorized":
        return (
          <div className="tokenLogin_content">
            <UserRoundX
              aria-hidden="true"
              className="tokenLogin_state_icon tokenLogin_state_icon--unauthorized"
            />
            <p className="tokenLogin_status">{unauthorizedMessage}</p>
            <a
              href={MEDITONG_RETURN_URL}
              className="tokenLogin_back_btn"
            >
              <ArrowLeft aria-hidden="true" className="size-4" />
              메디통으로 돌아가기
            </a>
          </div>
        );
      case "error":
        return (
          <div className="tokenLogin_content">
            <CircleAlert
              aria-hidden="true"
              className="tokenLogin_state_icon tokenLogin_state_icon--error"
            />
            <p className="tokenLogin_status">잘못된 로그인 정보입니다.</p>
            <a
              href={MEDITONG_RETURN_URL}
              className="tokenLogin_back_btn"
            >
              <ArrowLeft aria-hidden="true" className="size-4" />
              메디통으로 돌아가기
            </a>
          </div>
        );
      case "pending":
        return (
          <div className="tokenLogin_content">
            <p className="tokenLogin_status">
              로그인 정보를 불러오고 있습니다.
            </p>
            <TokenLoginSpinner />
          </div>
        );
      default: {
        const exhaustiveStatus: never = status;
        return exhaustiveStatus;
      }
    }
  })();

  return (
    <>
      <div className="contents" aria-hidden="true" inert>
        <Index />
      </div>
      {createPortal(
        status === "select" ? (
          content
        ) : (
          <section
            className={`tokenLogin_container tokenLogin_container--${status}`}
            aria-live="polite"
            aria-label="토큰 로그인 상태"
          >
            {content}
          </section>
        ),
        document.body
      )}
    </>
  );
}
